import { MaterialIcon, MaterialNameType } from "@website/icons/material";
import { SnackBarProperties } from "@website/ui/snackbar-v2/types";
import { deepCompare } from "@website/utils/deep-compare";
import { memo, useEffect, useRef } from "react";

const SnackBarV2 = ({
  severity = "default",
  title,
  Action,
  size = "auto",
  onClose,
  duration,
  disableLineClamp
}: SnackBarProperties) => {
  const iconTypeConfig = {
    default: "info",
    warning: "warning_amber",
    error: "error_outline",
    success: "check_circle"
  };
  const iconStyleConfig = {
    default: "text-on-color-high-emphasis",
    warning: "text-inverse-warning",
    success: "text-success",
    error: "text-inverse-error"
  };

  const timeout = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
    timeout.current = setTimeout(() => {
      onClose(title);
    }, duration || 3000);

    return () => {
      clearTimeout(timeout.current);
    };
  }, [duration, onClose, title]);

  if (title) {
    return (
      <div
        className={`${title ? "translate-x-1/2" : "translate-x-[300%]"} ${
          size === "multi-line" ? "flex-col px-4 pb-1" : "pl-2 pr-4"
        } bg-inverse-surface fixed bottom-6 right-1/2 z-[60] flex w-full max-w-[90%] gap-1 rounded-lg lg:w-[526px]`}
      >
        <div className="flex flex-1 items-center justify-start gap-2 py-4">
          <div
            className={`flex items-center justify-center text-2xl leading-6 ${iconStyleConfig[severity]}`}
          >
            <MaterialIcon
              name={iconTypeConfig[severity] as MaterialNameType}
              variant="outlined"
            />
          </div>

          <div
            className={`text-on-color-high-emphasis text-caption ${
              disableLineClamp ? "" : "line-clamp-3"
            }`}
          >
            {title}
          </div>
        </div>
        <div
          className={`flex items-center justify-end ${
            size === "multi-line" ? "gap-6" : "gap-4"
          } p-2`}
        >
          {Action}
          <button onClick={() => onClose(title)} tabIndex={0}>
            <MaterialIcon
              className="text-on-color-high-emphasis text-2xl leading-6"
              name="close"
            />
          </button>
        </div>
      </div>
    );
  }
};

const SnackBarMemoized = memo(SnackBarV2, deepCompare);
export { SnackBarMemoized as SnackBarV2 };
